import { Link } from "react-router-dom";
import { ArrowRight, Check, FileSearch, PenLine, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { BOOKING_URL, PRICING_PLANS } from "@/lib/pricing";

const ICONS = { write: PenLine, review: FileSearch, extend: Sparkles } as const;

const Pricing = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-1">
      <PageHero
        eyebrow="Pricing"
        title={
          <>
            Simple pricing for <span className="text-gold-gradient">every stage of your book.</span>
          </>
        }
        intro="Write, Review and Extend are priced separately, so you only pay for the stage you are in. Prices are in US dollars and match what you see inside each product."
      />

      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="grid gap-6 lg:grid-cols-3">
            {PRICING_PLANS.map((plan) => {
              const Icon = ICONS[plan.id];
              const external = plan.cta.href.startsWith("http");
              return (
                <article key={plan.id} className="card-noir flex flex-col p-8">
                  <Icon className="h-7 w-7 text-gold mb-6" aria-hidden />
                  <p className="eyebrow mb-3">{plan.stage}</p>
                  <h2 className="text-2xl font-semibold mb-4">{plan.name}</h2>
                  <p className="mb-1">
                    <span className="text-4xl font-semibold">{plan.price}</span>{" "}
                    <span className="text-sm text-muted-foreground">{plan.priceNote}</span>
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-4 mb-6">{plan.summary}</p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f) => (
                      <li key={f} className="flex gap-3 text-sm">
                        <Check className="h-4 w-4 shrink-0 text-gold mt-0.5" aria-hidden />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="mt-auto w-full bg-gold text-gold-foreground hover:bg-gold-light font-semibold">
                    {external ? (
                      <a href={plan.cta.href} target="_blank" rel="noopener noreferrer">
                        {plan.cta.label} <ArrowRight aria-hidden />
                      </a>
                    ) : (
                      <Link to={plan.cta.href}>
                        {plan.cta.label} <ArrowRight aria-hidden />
                      </Link>
                    )}
                  </Button>
                </article>
              );
            })}
          </div>

          <div className="mt-14 text-center">
            <p className="text-muted-foreground mb-6">Not sure which stage you are in? We will tell you in one call.</p>
            <Button asChild size="lg" variant="outline">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book a Call
              </a>
            </Button>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default Pricing;
